import { createAuthClient } from 'better-auth/react'
import { anonymousClient, magicLinkClient } from 'better-auth/client/plugins'
import useSWR from 'swr'

import { fetcher } from './swr'

export const authClient = createAuthClient({
  plugins: [magicLinkClient(), anonymousClient()],
})

export const { useSession, signOut } = authClient

export type MeResponse = {
  user: {
    id: string
    email: string
    name: string
    tenantId: number | null
    isAnonymous: boolean
  } | null
  tenant: { id: number; name: string; hasImage?: boolean } | null
  googleEnabled: boolean
  hasPassword: boolean
}

export type PublicConfig = {
  googleEnabled: boolean
  googleClientId: string | null
}

export const CONFIG_KEY = '/api/config'
export const ME_KEY = '/api/me'

export function useConfig() {
  return useSWR<PublicConfig>(CONFIG_KEY, fetcher)
}

export function useMe() {
  return useSWR<MeResponse>(ME_KEY, fetcher)
}

export async function fetchConfig(): Promise<PublicConfig> {
  return fetcher(CONFIG_KEY)
}

export async function fetchMe(): Promise<MeResponse> {
  return fetcher(ME_KEY)
}

/** Signed-in user still needs to create or join a household. */
export function needsHousehold(me: MeResponse | undefined): boolean {
  const user = me?.user
  return Boolean(user && !user.isAnonymous && user.tenantId == null)
}

/** Default landing for signed-in users with a household. */
export const DEFAULT_APP_PATH = '/planning'

/** Post-auth destination; defaults to planning when the user already has a household. */
export function postSignInPath(
  me: MeResponse | undefined,
  next = DEFAULT_APP_PATH
): string {
  if (!me?.user) return '/welcome'
  if (needsHousehold(me)) return '/onboarding'
  return next
}

/** Start a guest session, or reuse one if already anonymous. */
export async function ensureAnonymous() {
  const me = await fetchMe()
  if (me.user) {
    return { data: { user: me.user }, error: null as null }
  }
  const result = await authClient.signIn.anonymous()
  // Race / double-submit: session appeared between fetchMe and signIn
  if (result.error) {
    const again = await fetchMe()
    if (again.user?.isAnonymous) {
      return { data: { user: again.user }, error: null as null }
    }
  }
  return result
}

/** @deprecated use ensureAnonymous */
export const startAnonymous = ensureAnonymous

export async function signInWithGoogle(callbackURL = DEFAULT_APP_PATH) {
  return authClient.signIn.social({
    provider: 'google',
    callbackURL,
  })
}

function loadGsi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.google?.accounts?.id) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-google-gsi]'
    )
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject())
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.dataset.googleGsi = '1'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google GSI failed to load'))
    document.head.appendChild(script)
  })
}

/** Google One Tap (when GOOGLE_CLIENT_ID is configured). */
export async function promptGoogleOneTap(callbackURL = DEFAULT_APP_PATH) {
  try {
    const config = await fetchConfig()
    if (!config.googleClientId) return
    await loadGsi()
    await new Promise<void>((resolve) => {
      window.google!.accounts.id.initialize({
        client_id: config.googleClientId!,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: async (response: { credential: string }) => {
          const { error } = await authClient.signIn.social({
            provider: 'google',
            idToken: { token: response.credential },
          })
          if (!error) {
            window.location.href = callbackURL
          }
          resolve()
        },
      })
      window.google!.accounts.id.prompt(() => resolve())
    })
  } catch {
    // Unavailable / blocked — ignore
  }
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: Record<string, unknown>) => void
          prompt: (cb?: (notification: unknown) => void) => void
        }
      }
    }
  }
}
